import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { CheckCircleIcon } from '@phosphor-icons/react/CheckCircle';
import { MinusCircleIcon } from '@phosphor-icons/react/MinusCircle';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AudienceListType, FormSegment } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import { ListType } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import { LocationSegmentsListItem } from './LocationSegmentsListItem.tsx';

export type BaseLocationProps = {
  locationSegments: FormSegment[];
  listType: AudienceListType;
  dataTest?: string;
};

export type LocationSegmentsListProps =
  | (BaseLocationProps & { readOnly: true })
  | (BaseLocationProps & {
      readOnly?: false;
      onSegmentDelete: (segment: FormSegment) => void;
    });

export const LocationSegmentsList = ({ locationSegments, listType, dataTest, ...rest }: LocationSegmentsListProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [showMoreItems, setShowMoreItems] = useState<boolean>(false);

  const countAllSegments = locationSegments?.length;

  const splitAllSegments = useMemo(() => {
    return countAllSegments > 10 ? [locationSegments.slice(0, 10), locationSegments.slice(10)] : [locationSegments];
  }, [countAllSegments, locationSegments]);

  if (!locationSegments?.length) {
    return null;
  }

  const listTypeLabel = listType === 'includes' ? t('included') : t('excluded');

  const readOnly = rest.readOnly === true;
  const onSegmentDelete = !readOnly && 'onSegmentDelete' in rest ? rest.onSegmentDelete : undefined;

  return (
    <>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
        }}
      >
        {listTypeLabel}
      </Typography>

      <Grid container data-test={`${dataTest}-list`}>
        <Grid sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
          {listType === ListType.INCLUDES ? (
            <CheckCircleIcon data-test="check-icon" color={theme.palette.success.main} size={20} />
          ) : (
            <MinusCircleIcon data-test="block-icon" color={theme.palette.error.main} size={20} />
          )}
        </Grid>

        {splitAllSegments[0].map((segment, index) => (
          <LocationSegmentsListItem
            key={segment.id}
            item={segment}
            onItemDelete={onSegmentDelete}
            isLastItem={index === splitAllSegments[0].length - 1 && !showMoreItems}
            dataTest={dataTest}
          />
        ))}

        {showMoreItems &&
          splitAllSegments[1]?.map((segment, index) => (
            <LocationSegmentsListItem
              key={segment.id}
              item={segment}
              onItemDelete={onSegmentDelete}
              isLastItem={index === splitAllSegments[1].length - 1}
              dataTest={dataTest}
            />
          ))}
      </Grid>

      {countAllSegments > 10 && (
        <>
          {!showMoreItems && (
            <Grid container>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >{`and ${splitAllSegments[1]?.length} more.`}</Typography>
            </Grid>
          )}
          <Grid container>
            <Button
              variant="text"
              color="primary"
              onClick={() => setShowMoreItems(!showMoreItems)}
              data-test={'group-add-subgroup-button'}
            >
              {showMoreItems ? t('Hide') : t('Show More')}
            </Button>
          </Grid>
        </>
      )}
    </>
  );
};
